// Global
import { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

// Modules
import { selectCurrentPage } from '@/modules/Pages/feature';
import FileUpload from '@/modules/Pages/components/FileUpload/FileUpload';
import { RECOMMENDED_FORMS_AND_DOCS_FILE_TYPES } from '@/modules/Pages/utils/constants';

// Store
import { useAppState } from '@/store';

// Component
import { Input, TextArea } from '@/ui';
import { useFormInstance, useWatchForm } from '@/components';
import { VariableType } from 'src/shared/ui/VariablesDetector/VariableDetector.typedefs';
import { WideFormItem } from '../../../UpdatePage.styles';

// Helpers
import { usePageValidations } from '../../../hooks/usePageValidations';

interface UpdateContentFileBlockProps {
  fieldName: string | number;
  registerInputRef: (fieldName: string, ref: any, type: VariableType) => void;
  handleInputChange: (fieldName: string, value: string) => void;
}

export const UpdateContentFileBlock: FC<UpdateContentFileBlockProps> = ({
  fieldName,
  handleInputChange,
  registerInputRef,
}) => {
  const { t } = useTranslation(['common']);
  const { getRulesFor, getAcceptTypesFor } = usePageValidations();
  const currentPage = useAppState(selectCurrentPage);
  const form = useFormInstance();
  const fileName = useWatchForm(['content', 'blocks', fieldName, 'fileName'], form);

  const handleChangeFile = useCallback(
    (file?: File | null) => {
      form.setFieldValue(['content', 'blocks', fieldName, 'fileName'], file?.name ?? null);
    },
    [form, fieldName],
  );

  if (!currentPage) return null;

  return (
    <>
      <WideFormItem
        label={t('update_page_form_content_title')}
        name={[fieldName, 'title']}
        rules={getRulesFor(['content', 'blocks', 'title'])}
      >
        <Input
          ref={(ref) =>
            registerInputRef(`${t('update_page_form_content_title')} [${+fieldName + 1}]`, ref, VariableType.INPUT)
          }
          onChange={(e) =>
            handleInputChange(`${t('update_page_form_content_title')} [${+fieldName + 1}]`, e.target.value)
          }
          placeholder={t('update_page_form_content_title_placeholder')}
        />
      </WideFormItem>
      <WideFormItem
        label={t('update_page_form_content_description')}
        name={[fieldName, 'description']}
        rules={getRulesFor(['content', 'blocks', 'description'])}
      >
        <TextArea
          ref={(ref) =>
            registerInputRef(
              `${t('update_page_form_content_description')} [${+fieldName + 1}]`,
              ref,
              VariableType.TEXT_AREA,
            )
          }
          onChange={(e) =>
            handleInputChange(`${t('update_page_form_content_description')} [${+fieldName + 1}]`, e.target.value)
          }
          placeholder={t('update_page_form_content_description_placeholder')}
          rows={3}
        />
      </WideFormItem>

      <WideFormItem
        label={t('update_page_form_content_file')}
        name={[fieldName, 'file']}
        rules={getRulesFor(['content', 'blocks', 'file'])}
      >
        <FileUpload
          fileName={fileName}
          onChange={handleChangeFile}
          recommendedFormats={RECOMMENDED_FORMS_AND_DOCS_FILE_TYPES}
          accept={getAcceptTypesFor(['content', 'blocks', 'file'])}
        />
      </WideFormItem>
    </>
  );
};
